import type { AxiosInstance, AxiosResponse } from "axios";
import type { InterceptorConfig } from "../../createApi.ts";
import type { NetworkError } from "../../Error/NetworkError";
import type { ServerError } from "../../Error/ServerError";
import { InterceptorType } from "../../createApi.ts";
import { ClientError } from "../../Error/ClientError";
import { HttpStatusCode } from "../../HttpStatusCode.ts";

/**
 * Per-request opt-out for the interceptor below, for best-effort calls made while the session is being
 * torn down. Refreshing there would mint a fresh token mid-logout, or fail and start a second logout
 * that waits on the refresh already in flight.
 */
export interface SkipTokenRefreshConfig {
  skipTokenRefresh?: boolean
}

export function RefreshTokenResponseInterceptor(
  axiosInstance: AxiosInstance,
  refreshToken: () => Promise<void>,
  // A string entry matches the request URL exactly; a RegExp entry is tested against it.
  // The RegExp form is required for routes with a dynamic segment.
  excludedRoutes: (string | RegExp)[] = [],
): InterceptorConfig<InterceptorType.RESPONSE> {
  function isExcludedRoute(url: string): boolean {
    return excludedRoutes.some(route =>
      typeof route === "string" ? url === route : route.test(url),
    );
  }

  async function rejected(failedRequest: ClientError | ServerError | NetworkError): Promise<AxiosResponse<any, any>> {
    // We can't refresh the token if it's not a client error.
    if (!(failedRequest instanceof ClientError)) {
      throw failedRequest;
    }

    if (
      !failedRequest.config.url
      || failedRequest.response.status !== HttpStatusCode.HTTP_UNAUTHORIZED
      || isExcludedRoute(failedRequest.config.url)
      || (failedRequest.config as SkipTokenRefreshConfig).skipTokenRefresh === true
      || (failedRequest.config as any).retry === true
    ) {
      throw failedRequest;
    }

    await refreshToken();
    const config = { ...failedRequest.config, retry: true };

    return axiosInstance.request(config as any);
  }

  return {
    name: "RefreshTokenResponseInterceptor",
    type: InterceptorType.RESPONSE,
    priority: 100,
    rejected,
  };
}
